<template>
  <v-card class="mt-0">
    <v-skeleton-loader
      v-if="loading"
      :loading="loading"
      height="50"
      width="300"
      type="list-item"
    />
    <v-card-title class="pt-6">
      <span v-if="company">{{ company.name }}</span>
    </v-card-title>
    <v-card-text class="pt-0">
      <!-- SET PROVIDER LISTINGS TO UNCLAIMED/BASIC -->
      <v-dialog
        v-if="host !== 'admin.rehab.com'"
        v-model="setUnclaimedBasicDialog"
        width="500"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ma-2"
            x-small
            outlined
            color="error"
            v-bind="attrs"
            v-on="on"
            >Set Unclaimed/Basic
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="primary white--text">
            Set listings to unclaimed and basic?
          </v-card-title>

          <v-card-text class="pt-4">
            <p class="mb-0">
              Are you sure you want to set this provider's listings to unclaimed
              and basic?
            </p>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              raised
              @click="setUnclaimedBasicDialog = false"
            >
              No
            </v-btn>
            <v-btn
              color="primary"
              raised
              :loading="settingUnclaimedLoading"
              @click="setUnclaimedBasic"
            >
              Yes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DELETE CONNECT ACCOUNT -->
      <v-dialog
        v-if="company && company.connectCompanyId"
        v-model="deleteConnectAccountDialog"
        width="500"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ma-2"
            x-small
            outlined
            color="error"
            v-bind="attrs"
            v-on="on"
            >Delete Provider Platform Account
          </v-btn>
        </template>
        <v-card>
          <v-card-title class="primary white--text">
            Delete Provider Platform account?
          </v-card-title>

          <v-card-text class="pt-4">
            <p class="mb-0">
              Are you sure you want to delete this provider? This action will
              remove all information about this provider from Rehab.com Connect
              and delete all associated users. This action CANNOT be undone, so
              use your superpowers wisely.
            </p>
            <p>
              Type "<strong>confirm</strong>" below and then click "YES" to
              delete.
            </p>
            <v-text-field label="Confirm" v-model="confirm"></v-text-field>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              raised
              @click="deleteConnectAccountDialog = false"
            >
              No
            </v-btn>
            <v-btn
              color="primary"
              raised
              :loading="deleteConnectAccountLoading"
              @click="deleteConnectAccount"
              :disabled="confirm !== 'confirm'"
            >
              Yes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- COMPANY IN SYMFONY DATABASE -->
      <v-row>
        <v-col xl="4" lg="5" md="6" sm="7">
          <v-card class="pa-2" outlined>
            <v-card-title>
              <v-toolbar-title>Website Info</v-toolbar-title>
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader
                v-if="loading"
                :loading="loading"
                height="100"
                type="list-item-three-line"
              />

              <v-simple-table v-if="company" dense>
                <tbody>
                  <tr>
                    <th>ID</th>
                    <td>{{ company.id }}</td>
                  </tr>

                  <tr>
                    <th>Hubspot Id</th>
                    <td>{{ company.hubspotCompanyId }}</td>
                  </tr>

                  <tr>
                    <th>Domain Name</th>
                    <td>{{ company.companyDomainName }}</td>
                  </tr>

                  <tr>
                    <th>Description</th>
                    <td>
                      <span v-if="company.description">{{
                        decodeURIComponent(
                          JSON.parse(
                            '"' +
                              company.description.replace(/\"/g, '\\"') +
                              '"'
                          )
                        )
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- COMPANY IN CONNECT DATABASE -->
        <v-col xl="3" lg="3" md="6" sm="5">
          <v-card class="pa-2" outlined>
            <v-card-title>
              <v-toolbar-title>Provider Platform Info</v-toolbar-title>
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader
                v-if="loading"
                :loading="loading"
                height="100"
                type="list-item-three-line"
              />
              <v-simple-table v-if="company && company.connectCompanyId" dense>
                <tbody>
                  <!-- PROVIDER PLATFORM COMPANY ID-->
                  <tr>
                    <th>ID</th>
                    <td>
                      <span v-if="company.connectCompanyId">{{
                        company.connectCompanyId
                      }}</span>
                    </td>
                  </tr>

                  <!-- COMPANY NAME-->
                  <tr>
                    <th>Name</th>
                    <td>
                      <span v-if="company.connectCompanyId">{{
                        company.name
                      }}</span>
                    </td>
                  </tr>

                  <!-- ACCOUNT ID-->
                  <tr>
                    <th>AccountId</th>
                    <td>
                      <span v-if="company.connectCompanyId">{{
                        company.connectAccountId
                      }}</span>
                    </td>
                  </tr>

                  <!-- HUBSPOT ID-->
                  <tr>
                    <th>Hubspot Id</th>
                    <td>
                      <span v-if="company.connectCompanyId">{{
                        company.hubspotCompanyId
                      }}</span>
                    </td>
                  </tr>

                  <!-- PAYMENT TERMS-->
                  <tr>
                    <th>Payment Terms</th>
                    <td>
                      <span v-if="company.connectPaymentTerm">
                        {{ company.connectPaymentTerm }}
                      </span>
                      <span v-else-if="company.connectAccountId">
                        Receipt
                      </span>
                      <span v-else></span>
                    </td>
                  </tr>

                  <!-- FREE ACCOUNT? -->
                  <tr>
                    <th>Free Account</th>
                    <td>
                      <span v-if="company.freeUntil"
                        >Until {{ company.freeUntil | date }}</span
                      >
                      <span v-else>No</span>
                    </td>
                  </tr>

                  <!-- CLAIM DATE -->
                  <tr>
                    <th>Claim Date</th>
                    <td>
                      <span v-if="company.connectCompanyId">{{
                        company.claimedAt | date
                      }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <span v-else
                >This provider is not setup in the Provider Platform yet.</span
              >
            </v-card-text>
          </v-card>
        </v-col>

        <v-col
          xl="5"
          lg="5"
          md="6"
          sm="12"
          v-if="company && company.connectAccountId"
        >
          <v-card class="pa-2" outlined>
            <v-card-title>
              <v-toolbar-title>Provider Platform Notifications</v-toolbar-title>
            </v-card-title>
            <v-card-text>
              <v-skeleton-loader
                v-if="loading"
                :loading="loading"
                height="100"
                type="list-item-three-line"
              />
              <v-simple-table v-if="company" dense>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="notification in company.notifications"
                    :key="notification.id"
                  >
                    <td>{{ notification.createdAt | dateTime }}</td>
                    <td>{{ notification.description }}</td>
                    <td>{{ notification.status ? "Viewed" : "Not Viewed" }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  getCompany,
  setCompanyUnclaimedBasic,
  deleteCompany
} from "../../api/company";

export default {
  name: "ProviderInfo",
  title: "Provider Info",

  data() {
    return {
      loading: false,
      setUnclaimedBasicDialog: false,
      settingUnclaimedLoading: false,
      deleteConnectAccountDialog: false,
      deleteConnectAccountLoading: false,
      host: null,
      company: null,
      confirm: ""
    };
  },

  props: {
    companyId: [String, Number]
  },

  mounted() {
    this.host = window.location.host;
    this.getCompany();
  },

  methods: {
    async getCompany() {
      const companyId = parseInt(this.companyId);
      if (companyId) {
        this.loading = true;
        const result = await getCompany(companyId);
        this.company = result;
        this.loading = false;
      }
    },
    async setUnclaimedBasic() {
      const companyId = parseInt(this.companyId);
      if (companyId && this.company && this.company.connectCompanyId) {
        try {
          this.settingUnclaimedLoading = true;
          await setCompanyUnclaimedBasic(companyId);
          this.settingUnclaimedLoading = false;
          this.setUnclaimedBasicDialog = false;
        } catch (e) {
          console.error(e);
          this.settingUnclaimedLoading = false;
          this.setUnclaimedBasicDialog = false;
        }
      }
    },
    async deleteConnectAccount() {
      const companyId = parseInt(this.companyId);
      if (companyId && this.company && this.company.connectCompanyId) {
        try {
          this.deleteConnectAccountLoading = true;
          await deleteCompany(companyId);
          await this.getCompany();
          this.deleteConnectAccountDialog = false;
          this.deleteConnectAccountLoading = false;
        } catch (e) {
          this.deleteConnectAccountLoading = false;
          console.log("ERROR DELETING CONNECT ACCOUNT", e);
        }
      }
    }
  }
};
</script>
